import { reactive } from "vue";
import { api } from "@/lib/api";

// Singleton auth state shared across the app (no Pinia needed for this scope).
export const auth = reactive({
  user: null,
  organisation: null,
  membership: null,
  recentlyReauthenticated: false,
  ready: false,
});

function apply(payload) {
  auth.user = payload?.user ?? null;
  auth.organisation = payload?.organisation ?? null;
  auth.membership = payload?.membership ?? null;
  auth.recentlyReauthenticated = payload?.recently_reauthenticated ?? false;
}

export function isAuthenticated() {
  return Boolean(auth.user);
}

// The account-security nudge (SecurityBanner) can be dismissed for the current login
// period. sessionStorage survives reloads within the tab (so the dismissal sticks
// while navigating), but a brand-new browser session starts empty. We also clear it
// at every login/logout boundary so a re-sign-in always surfaces it again.
const SECURITY_NUDGE_KEY = "pandig:security-nudge-dismissed";

export function securityNudgeDismissed() {
  return sessionStorage.getItem(SECURITY_NUDGE_KEY) === "1";
}

export function dismissSecurityNudge() {
  sessionStorage.setItem(SECURITY_NUDGE_KEY, "1");
}

function resetSecurityNudge() {
  sessionStorage.removeItem(SECURITY_NUDGE_KEY);
}

// Bootstrap on app load — always resolves (200 {user:null} when signed out).
export async function fetchSession() {
  try {
    apply(await api.get("/api/session"));
  } catch {
    apply(null);
  } finally {
    auth.ready = true;
  }
}

export async function signIn(credentials) {
  resetSecurityNudge();
  const payload = await api.post("/api/session", credentials);
  if (!payload.otp_required) apply(payload);
  return payload; // caller checks payload.otp_required to show the OTP step
}

export async function verifyOtp(code) {
  apply(await api.post("/api/otp_session", { code }));
}

export async function signInWithPasskey() {
  resetSecurityNudge();
  const options = await api.get("/api/passkey_session/new");
  const { startAuthentication } = await import("@simplewebauthn/browser");
  const credential = await startAuthentication({ optionsJSON: options });
  apply(await api.post("/api/passkey_session", { credential }));
}

export async function register(registration) {
  resetSecurityNudge();
  apply(await api.post("/api/registration", { registration }));
}

export async function acceptInvitation(token, params) {
  resetSecurityNudge();
  apply(await api.patch("/api/invitation_acceptance", { token, ...params }));
}

export async function reauthenticate(params) {
  const payload = await api.post("/api/identity/reauthentication", params);
  apply(payload);
  return payload;
}

export async function signOut() {
  try {
    await api.delete("/api/session");
  } finally {
    apply(null);
    resetSecurityNudge();
  }
}

export async function switchOrganisation(membershipId) {
  apply(await api.patch("/api/current_organisation", { membership_id: membershipId }));
}

// Identity mutations from the settings page. Each returns the fresh session payload
// (name/address/email all live on it), so callers never need a manual reload or a
// hand patch of auth.user to stay in sync.
export async function updateProfile(params) {
  const payload = await api.patch("/api/identity/profile", params);
  apply(payload);
  return payload;
}

export async function updateEmail(params) {
  const payload = await api.patch("/api/identity/email", params);
  apply(payload);
  return payload;
}

export async function updatePassword(params) {
  const payload = await api.patch("/api/identity/password", params);
  apply(payload);
  return payload;
}

// AVG art. 17. Irreversible, and the server ends the session itself — so there is no
// payload to apply, only local state to stop pretending. The caller sends the reader
// somewhere public afterwards; there is nothing signed-in left to look at.
export async function eraseAccount(emailAddress) {
  await api.delete("/api/identity/account", { email_address: emailAddress });
  apply(null);
  resetSecurityNudge();
}

// Ending the VvE's use of Pandig. The session survives (the person may sit in other
// VvE's), so the server hands back a session payload with the active organisation
// already re-derived — never one still pointing at the VvE that just went.
export async function eraseOrganisation(name) {
  const payload = await api.delete("/api/current_organisation", { name });
  apply(payload);
  return payload;
}

// Opzeggen and its two reversals. All three hand back a session payload with the VvE's
// new status on it, which is what every screen reads to know whether it may still offer
// its buttons — so applying the payload is the whole state update. Nothing here signs
// anybody out: a VvE that has left is still a place you can read.
export async function cancelOrganisation() {
  const payload = await api.post("/api/current_organisation/cancellation");
  apply(payload);
  return payload;
}

export async function withdrawCancellation() {
  const payload = await api.delete("/api/current_organisation/cancellation");
  apply(payload);
  return payload;
}

export async function reactivateOrganisation() {
  const payload = await api.post("/api/current_organisation/reactivation");
  apply(payload);
  return payload;
}

// "Mag hier nog iets gestart worden?" — the question every board screen asks before it
// offers a button. Kept here so no component has to know the three status strings.
export function organisationOpen() {
  return (auth.organisation?.status ?? "open") === "open";
}

export function organisationClosed() {
  return auth.organisation?.status === "closed";
}
